import React from 'react';
import { v4 as uuid } from 'uuid';
import { useRecoilState, useRecoilValue } from 'recoil';
import styled, { css } from 'styled-components';
import Button from '~/components/atom/Button';
import type {
  FormBuilder_Label_Intl,
  FormBuilder_Locale,
  FormBuilder_OptionList,
  FormBuilder_OptionList_Option,
  FormBuilder_OptionList_OptionFragment,
} from '~/graphql/types';
import Input from '~/components/molecule/Input';
import { localeToIntlKey, localeToLabel } from '../../../../constants';
import { isNil, lensIndex, over } from 'ramda';
import { optionListById } from '~/components/page/Forms/components/Builder/state/optionList';
import { formState as formStateAtom } from '~/components/page/Forms/components/Builder/state';
import { issuesByPath } from '~/components/page/Forms/components/Builder/state/issues';
import JustificationContainer from '~/components/atom/JustificationContainer';
import { Label } from '~/components/atom/Typography';
import Icon from '~/components/atom/Icon';
import Divider from '~/components/atom/Divider';
import LeadscoreInput from '~/components/organism/LeadscoreInput';

type Props = {
  listId: FormBuilder_OptionList['id'];
  blockKey: string;
  nodeId: string;
};

const text = {
  aiButtonTooltip:
    'When enabled, AI will automatically calculate and optimize lead scores, providing more accurate scoring than manual estimates',
  optionsLabel: 'Opties',
  estimatedLeadscoreLabel: 'Leadscore',
  aiLeadscoreLabel: 'AI leadscore',
  addOptionButtonLabel: 'Nieuwe optie toevoegen',
};

const OptionsEditor: React.FCC<Props> = ({ listId, nodeId, blockKey }) => {
  const formState = useRecoilValue(formStateAtom);
  const [optionList, setOptionList] = useRecoilState(optionListById(listId));
  const issues = useRecoilValue(issuesByPath([nodeId, blockKey]));

  const issueMap = issues.reduce(
    (acc, curr) => {
      if (curr && curr.optionKey && curr.localeKey && !isNil(curr.message)) {
        acc[curr.optionKey] = acc[curr.optionKey] ?? {};
        acc[curr.optionKey][curr.localeKey] = curr.message;
      }

      return acc;
    },
    {} as Record<string, Record<string, string>>,
  );

  const hasOptions = optionList?.options && optionList.options.length > 0;

  return (
    <JustificationContainer width="100%" gap="m" direction="column">
      <Divider margin={[null]} />
      <JustificationContainer align="center" gap="xs">
        <Icon name="list" />
        <Label margin={[null]} size="base">
          {text.optionsLabel}
        </Label>
      </JustificationContainer>

      {hasOptions && (
        <ListContainer>
          {optionList?.options.map(({ key, label, icon }, index) => {
            const showDivider =
              optionList?.options.length > 1 &&
              index !== optionList.options.length - 1;

            const onLeadscoreChange = (value: number | null) => {
              // @ts-ignore allow null value for leadscore, this is handled in the deserialization
              // src/components/page/Forms/utils/serializeFormBuilder/index.ts
              setOptionList(prev => {
                if (!prev) return prev;
                return {
                  ...prev,
                  options: prev.options.map(option =>
                    option.key === key
                      ? {
                          ...option,
                          score: value,
                        }
                      : option,
                  ),
                };
              });
            };

            const onLocaleChange = (
              event: React.ChangeEvent<HTMLInputElement>,
              locale: FormBuilder_Locale,
            ) => {
              const lens = lensIndex<FormBuilder_OptionList_Option>(index);

              setOptionList(prev => {
                if (!prev) return prev;
                const updatedOption: FormBuilder_OptionList_Option = {
                  ...prev.options[index],
                  key,
                  label: {
                    ...label,
                    [localeToIntlKey[locale]]: event.target.value,
                  },
                  icon,
                  __typename: 'FormBuilder_OptionList_Option',
                };

                const updatedOptions = over(
                  lens,
                  () => updatedOption,
                  prev.options,
                );

                return { ...prev, options: updatedOptions };
              });
            };

            const onDelete = () => {
              setOptionList(prev => {
                if (!prev) return prev;

                return {
                  ...prev,
                  options: prev.options.filter(option => option.key !== key),
                };
              });
            };

            return (
              <ListItem key={key}>
                <JustificationContainer align="end" width="100%" gap="xs">
                  {formState?.style.availableLocale.map(locale => (
                    <Input
                      label={{ text: localeToLabel[locale] }}
                      externalErrors={
                        issueMap[key] && issueMap[key][locale]
                          ? [issueMap[key][locale]]
                          : []
                      }
                      key={locale}
                      width="100%"
                      size="small"
                      onChange={event => onLocaleChange(event, locale)}
                      value={label[localeToIntlKey[locale]]}
                    />
                  ))}
                  <div>
                    <Button
                      icon="delete"
                      onClick={onDelete}
                      appearance="danger"
                      size="small"
                    />
                  </div>
                </JustificationContainer>
                <LeadscoreInput
                  score={{
                    value: optionList?.options[index].score,
                    onChange: onLeadscoreChange,
                  }}
                />
                {showDivider && <Divider margin={['xs', null, null, null]} />}
              </ListItem>
            );
          })}
        </ListContainer>
      )}
      <Button
        size="small"
        label={text.addOptionButtonLabel}
        icon="plus"
        onClick={() => {
          setOptionList(prev => {
            if (!prev) return prev;
            return {
              ...prev,
              options: [
                ...prev.options,
                createNewOption({
                  __typename: 'FormBuilder_Label_Intl',
                  en: `Option ${prev.options.length + 1}`,
                  nl: `Optie ${prev.options.length + 1}`,
                }),
              ],
            };
          });
        }}
      />
    </JustificationContainer>
  );
};

export const createNewOption = (
  label: FormBuilder_Label_Intl,
): FormBuilder_OptionList_OptionFragment => ({
  __typename: 'FormBuilder_OptionList_Option',
  key: uuid(),
  label,
  score: 0,
});

const ListContainer = styled.ul(
  ({ theme }) => css`
    list-style: none;
    padding: 0;
    margin: 0;
    width: 100%;
    display: flex;
    flex-direction: column;
    gap: ${theme.space('xs')};
  `,
);

const ListItem = styled.li(
  ({ theme }) => css`
    display: flex;
    flex-direction: column;
    gap: ${theme.space('xs')};
    width: 100%;
  `,
);

export default OptionsEditor;
